import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const GuestEditModal = ({ guest, onSave, onClose, isSaving, onUploadReceipt }) => {
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [form, setForm] = useState({
    guest_name: '',
    email: '',
    phone: '',
    attending_ceremony: false,
    attending_reception: false,
    dietary_restrictions: '',
    special_requests: '',
    number_of_guests: 1,
    category: 'Guest',
    status: 'new',
  });

  useEffect(() => {
    if (guest) {
      setForm({
        guest_name: guest.guest_name || '',
        email: guest.email || '',
        phone: guest.phone || '',
        attending_ceremony: !!guest.attending_ceremony,
        attending_reception: !!guest.attending_reception,
        dietary_restrictions: guest.dietary_restrictions || '',
        special_requests: guest.special_requests || '',
        number_of_guests: guest.number_of_guests || 1,
        category: guest.category || 'Guest',
        status: guest.status || 'confirmed',
      });
    }
  }, [guest]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-1200 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-romantic-lg border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            {guest ? 'Edit Guest' : 'Add New Guest'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <Input
            label="Guest Name *"
            value={form.guest_name}
            onChange={(e) => update('guest_name', e.target.value)}
            required
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
            />
            <Input
              label="Phone"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="+62..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="Guest">Guest</option>
                <option value="Family">Family</option>
                <option value="VIP">VIP</option>
                <option value="Friend">Friend</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => update('status', e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="new">New</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="declined">Declined</option>
              </select>
            </div>
          </div>

          <Input
            label="Number of Guests"
            type="number"
            min="1"
            value={form.number_of_guests}
            onChange={(e) => update('number_of_guests', parseInt(e.target.value) || 1)}
          />

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.attending_ceremony}
                onChange={(e) => update('attending_ceremony', e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm">Attending Ceremony</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.attending_reception}
                onChange={(e) => update('attending_reception', e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm">Attending Reception</span>
            </label>
          </div>

          <Input
            label="Dietary Restrictions"
            value={form.dietary_restrictions}
            onChange={(e) => update('dietary_restrictions', e.target.value)}
          />

          <Input
            label="Wedding Wishes"
            value={form.special_requests}
            onChange={(e) => update('special_requests', e.target.value)}
          />

          {/* Receipt Upload (only for existing guests) */}
          {guest && (
            <div className="pt-3 border-t border-border">
              <label className="block text-sm font-medium text-foreground mb-2">Gift Transfer Receipt</label>
              {guest.gift_receipt_url && (
                <div className="mb-2 p-2 bg-muted/30 rounded-lg">
                  <img src={guest.gift_receipt_url} alt="Receipt" className="max-h-32 rounded mx-auto" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  className="text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-border file:text-sm file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/80 flex-1"
                />
                {receiptFile && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!onUploadReceipt || !receiptFile) return;
                      setUploadingReceipt(true);
                      try {
                        await onUploadReceipt(guest.id, receiptFile);
                        setReceiptFile(null);
                      } finally {
                        setUploadingReceipt(false);
                      }
                    }}
                    disabled={uploadingReceipt}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {uploadingReceipt ? 'Uploading...' : 'Upload'}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !form.guest_name} className="flex-1">
              {isSaving ? 'Saving...' : (guest ? 'Update Guest' : 'Add Guest')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuestEditModal;
